import { ActionIcon, Alert, Anchor, Button, FileButton, Group, ScrollArea, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconInfoCircle, IconX } from '@tabler/icons-react'
import { useRef } from 'react'

import { allUploadAccept, FileUpload } from '@/components/FileUpload'
import { theme } from '@/configs/theme'
import { getResourceIcon } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import * as classes from '@/styles/BulkUploadWizard.css'
import {
  bulkUploadAcceptAttribute,
  type BulkUploadMode,
  fileTypeLabel,
  formatFileSize,
  type StagedFile,
  supportedTypeGroups,
  totalSize,
} from '@/utils/bulkContentUpload'

import {
  bulkUploadCopy,
  dropzoneTitle,
  selectedSummary,
  supportedTypesLabel,
  tooLargeWarning,
  unsupportedWarning,
} from './bulkUploadCopy'

export type SkippedFiles = { unsupported: string[]; tooLarge: string[] }

type SelectFilesStepProps = {
  mode: BulkUploadMode
  files: StagedFile[]
  skipped: SkippedFiles
  onAdd: (files: File[]) => void
  onRemove: (name: string) => void
  onRemoveAll: () => void
}

export function SelectFilesStep({ mode, files, skipped, onAdd, onRemove, onRemoveAll }: SelectFilesStepProps) {
  const copy = bulkUploadCopy[mode]
  const resetPicker = useRef<() => void>(null)

  return (
    <Stack gap="md">
      <FileUpload
        maxFiles={0}
        accept={allUploadAccept}
        actionHidden
        titles={{ idle: dropzoneTitle, accept: dropzoneTitle }}
        hint={
          <>
            <span className={classes.hintLine}>
              {copy.helper} {supportedTypesLabel}
            </span>
            <span
              role="list"
              className={classes.typeList}
              style={{ gridTemplateRows: `repeat(${Math.ceil(supportedTypeGroups.length / 2)}, auto)` }}>
              {supportedTypeGroups.map(({ label, extensions }) => (
                <span key={label} role="listitem" className={classes.typeItem}>
                  {label[0].toUpperCase() + label.slice(1)}: {extensions.join(', ')}
                </span>
              ))}
            </span>
          </>
        }
        // Classified by `addFiles` rather than by the dropzone, so a skipped file is named in
        // the warning instead of raising the single-upload toast.
        onDrop={() => {}}
        onReject={() => {}}
        onDropAny={(accepted, rejected) => onAdd([...accepted, ...rejected.map(r => r.file)])}
      />

      {(skipped.unsupported.length > 0 || skipped.tooLarge.length > 0) && (
        <Alert color="yellow" icon={<IconAlertTriangle />}>
          <Stack gap={4}>
            {skipped.unsupported.length > 0 && <Text size="sm">{unsupportedWarning(skipped.unsupported)}</Text>}
            {skipped.tooLarge.length > 0 && <Text size="sm">{tooLargeWarning(skipped.tooLarge)}</Text>}
          </Stack>
        </Alert>
      )}

      {files.length > 0 && (
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {selectedSummary(files.length, formatFileSize(totalSize(files)))}
            </Text>
            <Group gap="md">
              <FileButton
                multiple
                accept={bulkUploadAcceptAttribute}
                resetRef={resetPicker}
                onChange={picked => {
                  onAdd(picked)
                  resetPicker.current?.()
                }}>
                {props => (
                  <Button {...props} variant="light" size="xs">
                    Add More Files
                  </Button>
                )}
              </FileButton>
              <Anchor component="button" type="button" size="sm" c="red" onClick={onRemoveAll}>
                Remove All
              </Anchor>
            </Group>
          </Group>

          <ScrollArea.Autosize mah={theme.rem(280)}>
            <Stack gap={6}>
              {files.map(staged => {
                const Icon = getResourceIcon(staged.type)
                return (
                  <Group key={staged.file.name} className={classes.fileRow} wrap="nowrap" gap="sm">
                    <Icon size={20} stroke={1.5} aria-hidden="true" className={classes.fileIcon} />
                    <Stack gap={0} className={classes.fileText}>
                      <Text size="sm" truncate="end">
                        {staged.file.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {fileTypeLabel(staged.file.name)} · {formatFileSize(staged.file.size)}
                      </Text>
                    </Stack>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={`Remove ${staged.file.name}`}
                      onClick={() => onRemove(staged.file.name)}>
                      <IconX size={16} aria-hidden="true" />
                    </ActionIcon>
                  </Group>
                )
              })}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      )}

      <Alert color="indigo" variant="light" icon={<IconInfoCircle />}>
        <Text size="sm">{copy.selectNote}</Text>
      </Alert>
    </Stack>
  )
}
